import {
  FolderPath,
  isSameOrDescendantFolderPath,
  parentFolderPath,
} from '@lib/library/folderPath';
import { pluralize } from '@lib/utils/units';
import { LibraryFolderStore } from '@stores/Library/LibraryFolder';
import { DragEvent, useCallback, useRef, useState } from 'react';
import css from './MediaDragSource.module.scss';

/**
 * A media drag inside the library page, from a card or row onto a folder. A MIME type of our own
 * rather than `text/plain`, so a drop target can tell it apart during `dragover` (when only the
 * types are readable) and so the page-wide file drop overlay, which only reacts to `Files`, stays
 * out of it.
 */
const MEDIA_DRAG_TYPE = 'application/x-starzero-media-ids';

/** A folder pill on its way into another folder. */
const FOLDER_DRAG_TYPE = 'application/x-starzero-folder-path';

/**
 * The folder being dragged, for the drop targets' `dragover`: the browser only lets a drop target
 * read a drag's data on `drop`, but a folder target has to know earlier whether to accept — a
 * folder can't go into itself, into its own subfolder, or where it already is. Only ever set by a
 * drag that began on this page, so a module variable is the whole story.
 */
let draggedFolder: FolderPath | null = null;

/** Whether the folder at `dragged` can be moved into `target`. */
export const canMoveFolderInto = (dragged: FolderPath, target: FolderPath) =>
  !isSameOrDescendantFolderPath(target, dragged) && parentFolderPath(dragged) !== target;

/**
 * The small pill that follows the pointer instead of the browser's snapshot of the whole card,
 * which at grid size covers the folders it is being dropped on. Carries the card's own thumbnail
 * when one has loaded — only a decoded image can be in a drag image, since the browser snapshots
 * it synchronously.
 */
const buildDragPreview = (source: HTMLElement, label: string, count: number) => {
  const preview = document.createElement('div');
  preview.className = css.dragPreview;

  const thumbnail = Array.from(source.querySelectorAll('img')).find(
    (img) => img.complete && img.naturalWidth > 0,
  );
  if (thumbnail) {
    const image = document.createElement('img');
    image.src = thumbnail.currentSrc || thumbnail.src;
    image.className = css.dragPreviewThumbnail;
    preview.appendChild(image);
  }

  const text = document.createElement('span');
  text.className = css.dragPreviewLabel;
  text.textContent = label;
  preview.appendChild(text);

  if (count > 1) {
    const badge = document.createElement('span');
    badge.className = css.dragPreviewCount;
    badge.textContent = String(count);
    preview.appendChild(badge);
  }
  return preview;
};

/**
 * Starts dragging `mediaId` — or, when it is part of the selection, the whole selection, which
 * is what dragging a selected item means in every file manager.
 */
export const startMediaDrag = (
  event: DragEvent<HTMLElement>,
  mediaId: string,
  folder: LibraryFolderStore,
) => {
  const selection = folder.videoSelection;
  const ids = selection.isSelected(mediaId) ? [...selection.selectedItems] : [mediaId];

  event.dataTransfer.setData(MEDIA_DRAG_TYPE, JSON.stringify(ids));
  event.dataTransfer.effectAllowed = 'move';

  const media = folder.allFiles.find((file) => file._id === mediaId);
  const label =
    ids.length > 1 ? pluralize(ids.length, 'media') : (media?.metadata?.name ?? 'Media');
  const preview = buildDragPreview(event.currentTarget, label, ids.length);
  document.body.appendChild(preview);
  event.dataTransfer.setDragImage(preview, 20, 20);
  // The node is only needed until the browser has taken its snapshot, which it does synchronously.
  requestAnimationFrame(() => preview.remove());
};

/** Starts dragging the folder pill at `path`, previewed by its name like a media by its title. */
export const startFolderDrag = (event: DragEvent<HTMLElement>, path: FolderPath, name: string) => {
  event.dataTransfer.setData(FOLDER_DRAG_TYPE, path);
  event.dataTransfer.effectAllowed = 'move';
  draggedFolder = path;
  // Cleared on drop too (below): a pill moved into another folder has left the page before its
  // `dragend`, which then never reaches the document.
  document.addEventListener('dragend', () => (draggedFolder = null), { once: true });

  const preview = buildDragPreview(event.currentTarget, name, 1);
  document.body.appendChild(preview);
  event.dataTransfer.setDragImage(preview, 20, 20);
  requestAnimationFrame(() => preview.remove());
};

export const isMediaDrag = (event: DragEvent<HTMLElement>) =>
  event.dataTransfer.types.includes(MEDIA_DRAG_TYPE);

const isFolderDrag = (event: DragEvent<HTMLElement>) =>
  event.dataTransfer.types.includes(FOLDER_DRAG_TYPE);

export const readMediaDrag = (event: DragEvent<HTMLElement>): string[] => {
  const parsed: unknown = JSON.parse(event.dataTransfer.getData(MEDIA_DRAG_TYPE) || '[]');
  if (!Array.isArray(parsed) || !parsed.every((id) => typeof id === 'string')) {
    throw new Error(`Malformed media drag payload: ${JSON.stringify(parsed)}`);
  }
  return parsed;
};

export type FolderDropHandlers = {
  onDropMedia: (mediaIds: string[], target: FolderPath) => void;
  onDropFolder: (folderPath: FolderPath, target: FolderPath) => void;
};

/**
 * Makes an element a place media and folders can be dropped into `folderPath`. A folder that
 * can't go there is refused at `dragover` — not accepting the drag is what gives the pointer its
 * "no" cursor, rather than a drop that fails afterwards. `isOver` drives the highlight; the
 * counter keeps it lit while the pointer crosses the element's own children, each of which fires
 * its own enter/leave pair.
 */
export const useFolderDropTarget = (
  folderPath: FolderPath,
  { onDropMedia, onDropFolder }: FolderDropHandlers,
  enabled: boolean,
) => {
  const [isOver, setIsOver] = useState(false);
  const depth = useRef(0);

  const accepts = useCallback(
    (event: DragEvent<HTMLElement>) =>
      enabled &&
      (isMediaDrag(event) ||
        (isFolderDrag(event) &&
          draggedFolder !== null &&
          canMoveFolderInto(draggedFolder, folderPath))),
    [enabled, folderPath],
  );

  const onDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!accepts(event)) return;
      event.preventDefault();
      depth.current++;
      setIsOver(true);
    },
    [accepts],
  );

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!accepts(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    },
    [accepts],
  );

  const onDragLeave = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!accepts(event)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setIsOver(false);
    },
    [accepts],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!accepts(event)) return;
      event.preventDefault();
      depth.current = 0;
      setIsOver(false);
      if (isFolderDrag(event)) {
        // The folder `accepts` checked, rather than re-reading the drag's data.
        const dragged = draggedFolder;
        draggedFolder = null;
        if (dragged) onDropFolder(dragged, folderPath);
      } else {
        onDropMedia(readMediaDrag(event), folderPath);
      }
    },
    [accepts, folderPath, onDropMedia, onDropFolder],
  );

  return { isOver, dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop } };
};
